import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Download, Edit2, FileText, Search, Upload, UserPlus, Users } from 'lucide-react';
import { parseClientSpreadsheet } from '../services/clientImportService';
import { getClientDisplayName, getClientTransactionLabel, getTransactionClientAllocations } from '../lib/clientUtils';

const createEmptyClientForm = () => ({
  name: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  tags: '',
  notes: ''
});

const clientDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
});

const formatClientDate = (value, fallback = 'No sales yet') => {
  if (!value) return fallback;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return clientDateFormatter.format(parsedDate);
};

const looksLikeProductSale = (category, label) => {
  const categoryValue = String(category || '').toLowerCase();
  const labelValue = String(label || '').toLowerCase();

  return (
    /retail|product|productsales|productsales|stock/.test(categoryValue) ||
    /\b(product|retail|serum|cream|shampoo|conditioner|mask|kit|bundle)\b/.test(labelValue)
  );
};

const addPurchaseMetric = (bucket, label, amount) => {
  const normalizedLabel = String(label || '').trim();
  if (!normalizedLabel) return;

  if (!bucket[normalizedLabel]) {
    bucket[normalizedLabel] = { label: normalizedLabel, count: 0, revenue: 0 };
  }

  bucket[normalizedLabel].count += 1;
  bucket[normalizedLabel].revenue += Number(amount) || 0;
};

const getTransactionPaidAmount = (transaction) => {
  if (!transaction) return 0;

  const hasExplicitPaidAmount =
    transaction.amountPaid !== undefined &&
    transaction.amountPaid !== null &&
    String(transaction.amountPaid).trim() !== '';
  const explicitPaidAmount = Number(transaction.amountPaid);
  if (hasExplicitPaidAmount && Number.isFinite(explicitPaidAmount) && explicitPaidAmount >= 0) {
    return parseFloat(explicitPaidAmount.toFixed(2));
  }

  if (Array.isArray(transaction.paymentBreakdown) && transaction.paymentBreakdown.length > 0) {
    const totalPaid = transaction.paymentBreakdown.reduce((sum, entry) => {
      const amount = Number(entry?.amount);
      return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0);
    }, 0);
    return parseFloat(totalPaid.toFixed(2));
  }

  const fallbackAmount = Number(transaction.amount);
  return Number.isFinite(fallbackAmount) && fallbackAmount > 0
    ? parseFloat(fallbackAmount.toFixed(2))
    : 0;
};

const getTransactionSaleTotalAmount = (transaction) => {
  if (!transaction) return 0;

  const explicitSaleTotal = Number(transaction.saleTotalAmount ?? transaction.originalSaleAmount);
  if (Number.isFinite(explicitSaleTotal) && explicitSaleTotal > 0) {
    return parseFloat(explicitSaleTotal.toFixed(2));
  }

  const grossAmount = Number(transaction.grossAmount);
  if ((transaction.isPartPaymentSale || Number(transaction.outstandingAmount) > 0) && Number.isFinite(grossAmount) && grossAmount > 0) {
    return parseFloat(grossAmount.toFixed(2));
  }

  const fallbackAmount = Number(transaction.amount);
  return Number.isFinite(fallbackAmount) && fallbackAmount > 0
    ? parseFloat(fallbackAmount.toFixed(2))
    : 0;
};

const getTransactionOutstandingAmount = (transaction) => {
  if (!transaction) return 0;

  const explicitOutstandingAmount = Number(transaction.outstandingAmount);
  if (Number.isFinite(explicitOutstandingAmount) && explicitOutstandingAmount > 0) {
    return parseFloat(explicitOutstandingAmount.toFixed(2));
  }

  const totalAmount = getTransactionSaleTotalAmount(transaction);
  if (!Number.isFinite(totalAmount) || totalAmount <= 0) return 0;

  return parseFloat(Math.max(totalAmount - getTransactionPaidAmount(transaction), 0).toFixed(2));
};

function ClientInsightList({ title, entries, emptyMessage, renderValue }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h5 className="mb-3 font-medium text-gray-900">{title}</h5>
      <div className="space-y-2 text-sm">
        {entries.length > 0 ? entries.map((entry) => (
          <div key={entry.label} className="flex items-start justify-between gap-3">
            <span className="break-words text-gray-700">{entry.label}</span>
            <span className="shrink-0 text-gray-500">{renderValue(entry)}</span>
          </div>
        )) : (
          <div className="text-gray-500">{emptyMessage}</div>
        )}
      </div>
    </div>
  );
}

function ClientMetrics({ summary, formatCurrency }) {
  const metricCards = [
    {
      label: 'Revenue',
      value: formatCurrency(summary.totalRevenue)
    },
    {
      label: 'Outstanding',
      value: formatCurrency(summary.outstandingBalance)
    },
    {
      label: 'Visits',
      value: summary.transactionCount
    },
    {
      label: 'Average sale',
      value: formatCurrency(summary.averageSpend)
    },
    {
      label: 'Last purchase',
      value: formatClientDate(summary.lastPurchaseDate, '-')
    }
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      {metricCards.map((metric) => (
        <div key={metric.label} className="flex min-h-[96px] flex-col justify-between rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="text-[11px] uppercase leading-snug tracking-wide text-gray-500">{metric.label}</div>
          <div className="break-words text-base font-semibold leading-tight text-gray-900 sm:text-xl">
            {metric.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function ClientSalesList({ sales, formatCurrency, onDownloadInvoice, onEditTransaction }) {
  const [selectedSaleId, setSelectedSaleId] = useState(sales[0]?.id || '');
  const selectedSale = sales.find((sale) => sale.id === selectedSaleId) || sales[0] || null;

  useEffect(() => {
    if (sales.length === 0) {
      setSelectedSaleId('');
      return;
    }

    if (!sales.some((sale) => sale.id === selectedSaleId)) {
      setSelectedSaleId(sales[0].id);
    }
  }, [sales, selectedSaleId]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <h5 className="font-medium text-gray-900">Sales and invoices</h5>
        <span className="text-sm text-gray-500">
          {sales.length} {sales.length === 1 ? 'sale' : 'sales'}
        </span>
      </div>

      {sales.length > 0 ? (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Select sale</span>
            <select
              value={selectedSale?.id || ''}
              onChange={(event) => setSelectedSaleId(event.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              {sales.map((sale) => (
                <option key={sale.id} value={sale.id}>
                  {formatClientDate(sale.date, 'No date')} - {sale.description || 'Sale'} - {formatCurrency(sale.saleTotalAmount)}
                  {sale.outstandingAmount > 0.01 ? ` (${formatCurrency(sale.outstandingAmount)} outstanding)` : ''}
                </option>
              ))}
            </select>
          </label>

          {selectedSale && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    {formatClientDate(selectedSale.date, 'No date')}
                  </div>
                  <div className="mt-1 break-words font-medium text-gray-900">
                    {selectedSale.description || 'Sale'}
                  </div>
                  {selectedSale.jobReference && (
                    <div className="mt-0.5 text-xs text-gray-500">{selectedSale.jobReference}</div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm sm:flex sm:items-center sm:gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">Total</div>
                    <div className="font-semibold text-gray-900">{formatCurrency(selectedSale.saleTotalAmount)}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">Outstanding</div>
                    <div className={selectedSale.outstandingAmount > 0.01 ? 'font-semibold text-amber-700' : 'text-gray-600'}>
                      {formatCurrency(selectedSale.outstandingAmount)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onDownloadInvoice?.(selectedSale.transaction)}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 sm:flex-none"
                  >
                    <Download size={15} />
                    View invoice
                  </button>
                  <button
                    type="button"
                    onClick={() => onEditTransaction?.(selectedSale.transaction)}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100 sm:flex-none"
                  >
                    <Edit2 size={15} />
                    Edit
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
          No sales recorded for this client yet.
        </div>
      )}
    </div>
  );
}

function OutstandingPaymentRow({ transaction, formatCurrency, paymentMethods, onRecordPartPayment }) {
  const today = new Date().toISOString().split('T')[0];
  const [paymentAmount, setPaymentAmount] = useState(
    transaction.outstandingAmount > 0 ? transaction.outstandingAmount.toFixed(2) : ''
  );
  const [paymentDate, setPaymentDate] = useState(today);
  const [paymentMethod, setPaymentMethod] = useState(paymentMethods?.[0]?.id || 'cash');
  const [rctCreditAmount, setRctCreditAmount] = useState('');
  const isRctIncome = String(transaction.category || '').toLowerCase() === 'rct_contract_income' ||
    String(transaction.category || '').toLowerCase() === 'rcttaxincome' ||
    transaction.rctRole === 'subcontractor';

  const handleSubmit = () => {
    if (typeof onRecordPartPayment !== 'function') return;

    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a payment amount greater than 0.');
      return;
    }

    const rctCredit = isRctIncome ? Number(rctCreditAmount || 0) : 0;
    if (!Number.isFinite(rctCredit) || rctCredit < 0) {
      alert('Enter a valid RCT credit amount.');
      return;
    }

    if ((amount + rctCredit) - transaction.outstandingAmount > 0.01) {
      alert(`Payment cannot be more than ${formatCurrency(transaction.outstandingAmount)} outstanding.`);
      return;
    }

    onRecordPartPayment(transaction.id, {
      amount,
      rctCreditAmount: rctCredit,
      date: paymentDate,
      method: paymentMethod
    });
  };

  return (
    <div className="rounded-lg border border-amber-100 bg-white px-3 py-3 text-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="break-words font-medium text-gray-900">
            {transaction.description || 'Sale'}
          </div>
          <div className="mt-1 text-gray-500">
            Paid {formatCurrency(transaction.amountPaid)} of {formatCurrency(transaction.saleTotalAmount)}
          </div>
        </div>
        <span className="shrink-0 font-semibold text-amber-900">
          {formatCurrency(transaction.outstandingAmount)} outstanding
        </span>
      </div>

      <div className={`mt-3 grid grid-cols-1 gap-2 ${isRctIncome ? 'sm:grid-cols-[110px,110px,140px,1fr,auto]' : 'sm:grid-cols-[110px,140px,1fr,auto]'}`}>
        <input
          type="text"
          inputMode="decimal"
          value={paymentAmount}
          onChange={(event) => setPaymentAmount(event.target.value.replace(/[^\d.]/g, ''))}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          placeholder="Amount"
        />
        {isRctIncome && (
          <input
            type="text"
            inputMode="decimal"
            value={rctCreditAmount}
            onChange={(event) => setRctCreditAmount(event.target.value.replace(/[^\d.]/g, ''))}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder="RCT credit"
          />
        )}
        <input
          type="date"
          value={paymentDate}
          onChange={(event) => setPaymentDate(event.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
        <select
          value={paymentMethod}
          onChange={(event) => setPaymentMethod(event.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        >
          {(paymentMethods || []).map((method) => (
            <option key={method.id} value={method.id}>
              {method.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-green-700"
        >
          Record payment
        </button>
      </div>
    </div>
  );
}

function ClientDetails({ summary, formatCurrency, paymentMethods, onRecordPartPayment, onDownloadInvoice, onEditTransaction }) {
  return (
    <div className="space-y-4">
      <ClientMetrics summary={summary} formatCurrency={formatCurrency} />

      <ClientSalesList
        sales={summary.salesTransactions}
        formatCurrency={formatCurrency}
        onDownloadInvoice={onDownloadInvoice}
        onEditTransaction={onEditTransaction}
      />

      {summary.outstandingTransactions.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h5 className="font-medium text-amber-950">Outstanding payments</h5>
            <span className="text-sm font-semibold text-amber-950">
              {formatCurrency(summary.outstandingBalance)}
            </span>
          </div>
          <div className="space-y-2">
            {summary.outstandingTransactions.map((transaction) => (
              <OutstandingPaymentRow
                key={transaction.id}
                transaction={transaction}
                formatCurrency={formatCurrency}
                paymentMethods={paymentMethods}
                onRecordPartPayment={onRecordPartPayment}
              />
            ))}
          </div>
        </div>
      )}

      {summary.allocatedCosts.length > 0 && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h5 className="font-medium text-blue-950">Allocated job costs</h5>
            <div className="text-sm font-semibold text-blue-950">
              {formatCurrency(summary.allocatedCostTotal)}
              {summary.billableCostTotal > 0 && (
                <span className="ml-2 text-blue-700">
                  {formatCurrency(summary.billableCostTotal)} billable
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {summary.allocatedCosts.map((cost) => (
              <div key={cost.id} className="rounded-lg border border-blue-100 bg-white px-3 py-3 text-sm">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="break-words font-medium text-gray-900">
                      {cost.description}
                    </div>
                    <div className="mt-1 text-gray-500">
                      {formatClientDate(cost.date, 'No date')}
                      {cost.jobReference ? ` | ${cost.jobReference}` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-left sm:text-right">
                    <div className="font-semibold text-blue-950">{formatCurrency(cost.amount)}</div>
                    {cost.billableToClient && (
                      <div className="text-xs font-medium text-blue-700">Billable</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <ClientInsightList
          title="Services purchased"
          entries={summary.topTreatments}
          emptyMessage="No service data yet."
          renderValue={(entry) => `${entry.count}x`}
        />

        <ClientInsightList
          title="Products purchased"
          entries={summary.topProducts}
          emptyMessage="No product data yet."
          renderValue={(entry) => `${entry.count}x`}
        />

        <ClientInsightList
          title="Revenue by category"
          entries={summary.categoryRevenue}
          emptyMessage="No category breakdown yet."
          renderValue={(entry) => formatCurrency(entry.revenue)}
        />
      </div>

      {summary.client?.notes && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          {summary.client.notes}
        </div>
      )}
    </div>
  );
}

function ClientDatabasePanel({
  clients = [],
  transactions = [],
  formatCurrency,
  onAddClient,
  onUpdateClient,
  onDeleteClient,
  onImportClients,
  onStartSaleForClient,
  onStartInvoiceForClient,
  onDownloadInvoice,
  onEditTransaction,
  onRecordPartPayment,
  paymentMethods = [],
  onClose
}) {
  const [formState, setFormState] = useState(createEmptyClientForm());
  const [editingClientId, setEditingClientId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientKey, setSelectedClientKey] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [showMobileForm, setShowMobileForm] = useState(false);
  const [showMobileImport, setShowMobileImport] = useState(false);
  const fileInputRef = useRef(null);
  const formPanelRef = useRef(null);
  const importPanelRef = useRef(null);

  const clientSummaries = useMemo(() => {
    const clientsById = new Map();
    const clientsByName = new Map();
    const summaryMap = new Map();

    const ensureSummary = (key, client) => {
      if (!summaryMap.has(key)) {
        summaryMap.set(key, {
          key,
          client,
          totalRevenue: 0,
          outstandingBalance: 0,
          transactionCount: 0,
          averageSpend: 0,
          lastPurchaseDate: null,
          salesTransactions: [],
          outstandingTransactions: [],
          allocatedCosts: [],
          allocatedCostTotal: 0,
          billableCostTotal: 0,
          treatments: {},
          products: {},
          categories: {}
        });
      }

      const entry = summaryMap.get(key);
      if (client && !entry.client?.id) {
        entry.client = client;
      }
      return entry;
    };

    (Array.isArray(clients) ? clients : []).forEach((client) => {
      const displayName = getClientDisplayName(client);
      const key = `id:${client.id}`;
      clientsById.set(client.id, client);
      if (displayName) {
        clientsByName.set(displayName.toLowerCase(), client);
      }
      ensureSummary(key, client);
    });

    (Array.isArray(transactions) ? transactions : []).forEach((transaction) => {
      const transactionType = String(transaction?.type || '').toLowerCase();
      if (transactionType !== 'income' && transactionType !== 'expenses' && transactionType !== 'expense') return;

      const fallbackName = getClientTransactionLabel(transaction);
      const matchedClient =
        (transaction?.clientId && clientsById.get(transaction.clientId)) ||
        (fallbackName ? clientsByName.get(fallbackName.toLowerCase()) : null);

      if (transactionType === 'expenses' || transactionType === 'expense') {
        const amount = Number(transaction?.expenseTotalAmount ?? transaction?.amount) || 0;
        if (amount <= 0) return;

        const allocations = getTransactionClientAllocations(transaction, clientsById, amount);
        const summaryAllocations = allocations.length > 0
          ? allocations
          : matchedClient
            ? [{ clientId: matchedClient.id, clientName: getClientDisplayName(matchedClient), allocatedAmount: amount }]
            : fallbackName
              ? [{ clientId: '', clientName: fallbackName, allocatedAmount: amount }]
              : [];

        summaryAllocations.forEach((allocation) => {
          const allocationClient = allocation.clientId ? clientsById.get(allocation.clientId) : null;
          const allocationName = allocation.clientName || getClientDisplayName(allocationClient) || fallbackName;
          const summaryKey = allocationClient
            ? `id:${allocationClient.id}`
            : allocationName
              ? `name:${allocationName.toLowerCase()}`
              : '';

          if (!summaryKey) return;

          const summary = ensureSummary(summaryKey, allocationClient || {
            id: '',
            name: allocationName
          });
          const allocatedAmount = Number(allocation.allocatedAmount) || 0;

          const allocatedCost = {
            id: transaction.id,
            date: transaction.date || '',
            description: transaction.description || 'Expense',
            amount: allocatedAmount,
            jobReference: transaction.jobReference || '',
            billableToClient: transaction.billableToClient === true
          };

          summary.allocatedCosts.push(allocatedCost);
          summary.allocatedCostTotal += allocatedAmount;
          if (allocatedCost.billableToClient) {
            summary.billableCostTotal += allocatedAmount;
          }
        });
        return;
      }

      const summaryKey = matchedClient
        ? `id:${matchedClient.id}`
        : fallbackName
          ? `name:${fallbackName.toLowerCase()}`
          : '';

      if (!summaryKey) return;

      const summary = ensureSummary(summaryKey, matchedClient || {
        id: '',
        name: fallbackName
      });

      const isPartPaymentInstallment = transaction?.isPartPaymentInstallment === true;
      const amount = Number(transaction?.amount) || 0;
      const paidAmount = getTransactionPaidAmount(transaction);
      const outstandingAmount = getTransactionOutstandingAmount(transaction);
      const saleTotalAmount = getTransactionSaleTotalAmount(transaction);
      const transactionDate = transaction?.date ? new Date(transaction.date) : null;

      summary.totalRevenue += amount;
      if (!isPartPaymentInstallment) {
        summary.outstandingBalance += outstandingAmount;
        summary.transactionCount += 1;
        summary.salesTransactions.push({
          id: transaction.id,
          date: transaction.date || transaction.invoiceDate || '',
          description: transaction.service || transaction.description || 'Sale',
          jobReference: transaction.jobReference || '',
          saleTotalAmount,
          amountPaid: paidAmount,
          outstandingAmount,
          transaction
        });
      }

      if (!isPartPaymentInstallment && outstandingAmount > 0.01) {
        summary.outstandingTransactions.push({
          id: transaction.id,
          date: transaction.date || '',
          description: transaction.service || transaction.description || 'Sale',
          saleTotalAmount,
          amountPaid: paidAmount,
          outstandingAmount
        });
      }

      if (!isPartPaymentInstallment && transactionDate instanceof Date && !Number.isNaN(transactionDate.getTime())) {
        const currentLatest = summary.lastPurchaseDate ? new Date(summary.lastPurchaseDate) : null;
        if (!currentLatest || transactionDate > currentLatest) {
          summary.lastPurchaseDate = transaction.date;
        }
      }

      const addCategory = (category, value) => {
        const normalizedCategory = String(category || '').trim();
        if (!normalizedCategory) return;
        if (!summary.categories[normalizedCategory]) {
          summary.categories[normalizedCategory] = { label: normalizedCategory, revenue: 0 };
        }
        summary.categories[normalizedCategory].revenue += Number(value) || 0;
      };

      if (Array.isArray(transaction?.items) && transaction.items.length > 0) {
        transaction.items.forEach((item) => {
          const label = item?.description || transaction?.service || transaction?.description || 'Sale';
          const itemAmount = Number(item?.amount) || amount;
          const isProduct = looksLikeProductSale(item?.category || transaction?.category, label);

          addCategory(item?.category || transaction?.category, itemAmount);
          if (!isPartPaymentInstallment) {
            addPurchaseMetric(isProduct ? summary.products : summary.treatments, label, itemAmount);
          }
        });
      } else {
        const label = transaction?.service || transaction?.description || 'Sale';
        const isProduct = looksLikeProductSale(transaction?.category, label);
        addCategory(transaction?.category, amount);
        if (!isPartPaymentInstallment) {
          addPurchaseMetric(isProduct ? summary.products : summary.treatments, label, amount);
        }
      }
    });

    return [...summaryMap.values()]
      .map((summary) => ({
        ...summary,
        displayName: getClientDisplayName(summary.client) || summary.client?.name || 'Unnamed client',
        averageSpend: summary.transactionCount > 0
          ? summary.totalRevenue / summary.transactionCount
          : 0,
        outstandingBalance: parseFloat((summary.outstandingBalance || 0).toFixed(2)),
        allocatedCostTotal: parseFloat((summary.allocatedCostTotal || 0).toFixed(2)),
        billableCostTotal: parseFloat((summary.billableCostTotal || 0).toFixed(2)),
        salesTransactions: [...summary.salesTransactions]
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)),
        outstandingTransactions: [...summary.outstandingTransactions]
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)),
        allocatedCosts: [...summary.allocatedCosts]
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
          .slice(0, 10),
        topTreatments: Object.values(summary.treatments)
          .sort((a, b) => b.count - a.count || b.revenue - a.revenue)
          .slice(0, 5),
        topProducts: Object.values(summary.products)
          .sort((a, b) => b.count - a.count || b.revenue - a.revenue)
          .slice(0, 5),
        categoryRevenue: Object.values(summary.categories)
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 5)
      }))
      .sort((a, b) =>
        (b.totalRevenue - a.totalRevenue) ||
        a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
      );
  }, [clients, transactions]);

  const filteredSummaries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return clientSummaries;

    return clientSummaries.filter((summary) => {
      const client = summary.client || {};
      return [
        summary.displayName,
        client.email,
        client.phone,
        ...(client.tags || [])
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
    });
  }, [clientSummaries, searchTerm]);

  const selectedClientSummary = useMemo(
    () => filteredSummaries.find((entry) => entry.key === selectedClientKey) || null,
    [filteredSummaries, selectedClientKey]
  );

  useEffect(() => {
    if (!selectedClientKey) {
      return;
    }

    if (!selectedClientSummary) {
      setSelectedClientKey('');
    }
  }, [selectedClientKey, selectedClientSummary]);

  useEffect(() => {
    if (editingClientId) {
      setShowMobileForm(true);
      setShowMobileImport(false);
    }
  }, [editingClientId]);

  useEffect(() => {
    if (showMobileForm) {
      formPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showMobileForm]);

  useEffect(() => {
    if (showMobileImport) {
      importPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showMobileImport]);

  const linkedRevenue = clientSummaries.reduce((sum, entry) => sum + entry.totalRevenue, 0);
  const outstandingBalance = clientSummaries.reduce((sum, entry) => sum + entry.outstandingBalance, 0);
  const activeClientCount = clientSummaries.filter((entry) => entry.transactionCount > 0).length;
  const clientsWithOutstandingBalances = useMemo(
    () => clientSummaries
      .filter((entry) => Number(entry.outstandingBalance) > 0.01)
      .sort((a, b) => b.outstandingBalance - a.outstandingBalance || a.displayName.localeCompare(b.displayName)),
    [clientSummaries]
  );

  const resetForm = () => {
    setEditingClientId('');
    setFormState(createEmptyClientForm());
  };

  const openClientEditor = (client) => {
    setEditingClientId(client.id);
    setFormState({
      name: client.name || '',
      firstName: client.firstName || '',
      lastName: client.lastName || '',
      email: client.email || '',
      phone: client.phone || '',
      tags: Array.isArray(client.tags) ? client.tags.join(', ') : '',
      notes: client.notes || ''
    });
    setShowMobileForm(true);
    setShowMobileImport(false);
  };

  const clearClientForm = () => {
    resetForm();
    setShowMobileForm(false);
  };

  const handleDeleteClientAction = async (clientId, displayName, summaryKey) => {
    if (!window.confirm(`Delete ${displayName} from your client database? Historical transactions will be kept.`)) {
      return;
    }

    await onDeleteClient(clientId);

    if (selectedClientKey === summaryKey) {
      setSelectedClientKey('');
    }
    if (editingClientId === clientId) {
      resetForm();
    }

    setImportMessage('Client deleted.');
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!formState.name.trim() && !formState.firstName.trim() && !formState.lastName.trim()) {
      alert('Enter at least a client name before saving.');
      return;
    }

    if (editingClientId) {
      await onUpdateClient(editingClientId, formState);
      setImportMessage('Client updated.');
    } else {
      await onAddClient(formState);
      setImportMessage('Client added.');
    }

    resetForm();
    setShowMobileForm(false);
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportMessage('');

    try {
      const parsed = await parseClientSpreadsheet(file);
      const summary = await onImportClients(parsed.clients);
      const details = [
        `${summary.importedCount} client${summary.importedCount === 1 ? '' : 's'} imported`
      ];

      if (summary.updatedCount > 0) {
        details.push(`${summary.updatedCount} merged with existing records`);
      }
      if (parsed.skippedRows > 0) {
        details.push(`${parsed.skippedRows} row(s) skipped`);
      }

      setImportMessage(`${details.join(' | ')} from ${parsed.fileName || 'spreadsheet'}.`);
      setShowMobileImport(true);
    } catch (error) {
      console.error('Failed to import client spreadsheet:', error);
      setImportMessage(error?.message || 'Could not import that file.');
      setShowMobileImport(true);
    } finally {
      setIsImporting(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const summaryCards = [
    {
      label: 'Clients',
      value: clients.length
    },
    {
      label: 'Active clients',
      value: activeClientCount
    },
    {
      label: 'Linked revenue',
      value: formatCurrency(linkedRevenue)
    },
    {
      label: 'Outstanding',
      value: formatCurrency(outstandingBalance)
    },
    {
      label: 'Avg / active client',
      value: formatCurrency(activeClientCount > 0 ? linkedRevenue / activeClientCount : 0)
    }
  ];

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-6">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-gray-700" />
          <h3 className="text-lg font-medium text-gray-900">Clients</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-gray-500 transition hover:bg-gray-50 hover:text-gray-800"
        >
          Close
        </button>
      </div>

      <div className="space-y-5 p-4 sm:space-y-6 sm:p-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {summaryCards.map((card) => (
            <div key={card.label} className="flex min-h-[104px] flex-col justify-between rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-xs leading-snug text-gray-500 sm:text-sm">{card.label}</div>
              <div className="break-words text-xl font-semibold leading-tight text-gray-900 tabular-nums sm:text-2xl">
                {card.value}
              </div>
            </div>
          ))}
        </div>

        {clientsWithOutstandingBalances.length > 0 && (
          <div className="rounded-xl border border-pink-100 bg-gradient-to-br from-pink-50 via-white to-cyan-50 p-4">
            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-slate-950">Clients who owe money</h4>
                <p className="text-xs text-slate-600">Open a client to record a payment or review unpaid invoices.</p>
              </div>
              <span className="text-sm font-semibold text-slate-950">
                {formatCurrency(outstandingBalance)} total
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {clientsWithOutstandingBalances.slice(0, 6).map((summary) => (
                <button
                  key={summary.key}
                  type="button"
                  onClick={() => setSelectedClientKey(summary.key)}
                  className="flex min-h-[72px] items-center justify-between gap-3 rounded-lg border border-pink-100 bg-white/90 px-3 py-2 text-left shadow-sm transition hover:border-cyan-200 hover:bg-cyan-50/70"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-gray-900">{summary.displayName}</span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {summary.outstandingTransactions.length} unpaid {summary.outstandingTransactions.length === 1 ? 'invoice' : 'invoices'}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold text-cyan-950 tabular-nums">
                    {formatCurrency(summary.outstandingBalance)}
                  </span>
                </button>
              ))}
            </div>
            {clientsWithOutstandingBalances.length > 6 && (
              <div className="mt-2 text-xs text-slate-600">
                Showing top 6 of {clientsWithOutstandingBalances.length} clients with outstanding balances.
              </div>
            )}
          </div>
        )}

        {importMessage && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            {importMessage}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[360px,1fr] xl:gap-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 xl:hidden">
              <button
                type="button"
                onClick={() => {
                  setShowMobileForm((prev) => !prev);
                  setShowMobileImport(false);
                }}
                aria-expanded={showMobileForm}
                className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50/40"
              >
                <span>{editingClientId ? 'Edit client' : 'Add client'}</span>
                {showMobileForm ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowMobileImport((prev) => !prev);
                  setShowMobileForm(false);
                }}
                aria-expanded={showMobileImport}
                className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50/40"
              >
                <span>Bulk import</span>
                {showMobileImport ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            <div
              ref={formPanelRef}
              className={`${showMobileForm ? 'block' : 'hidden'} xl:block`}
            >
              <form onSubmit={handleFormSubmit} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2">
                  <UserPlus size={16} className="text-gray-700" />
                  <h4 className="font-medium text-gray-900">
                    {editingClientId ? 'Edit client' : 'Add client'}
                  </h4>
                </div>

                <input
                  type="text"
                  autoComplete="name"
                  value={formState.name}
                  onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder="Client name"
                />

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input
                    type="text"
                    autoComplete="given-name"
                    value={formState.firstName}
                    onChange={(event) => setFormState((prev) => ({ ...prev, firstName: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="First name"
                  />
                  <input
                    type="text"
                    autoComplete="family-name"
                    value={formState.lastName}
                    onChange={(event) => setFormState((prev) => ({ ...prev, lastName: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="Last name"
                  />
                </div>

                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={formState.email}
                  onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                  className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder="Email"
                />

                <input
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={formState.phone}
                  onChange={(event) => setFormState((prev) => ({ ...prev, phone: event.target.value }))}
                  className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder="Phone"
                />

                <input
                  type="text"
                  value={formState.tags}
                  onChange={(event) => setFormState((prev) => ({ ...prev, tags: event.target.value }))}
                  className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder="Tags separated by commas"
                />

                <textarea
                  value={formState.notes}
                  onChange={(event) => setFormState((prev) => ({ ...prev, notes: event.target.value }))}
                  rows={4}
                  className="min-h-[120px] w-full rounded-xl border border-gray-200 px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder="Notes"
                />

                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-warn-gradient px-4 py-3 font-medium text-[color:var(--color-text)] transition hover:brightness-95"
                  >
                    {editingClientId ? 'Save client' : 'Add client'}
                  </button>
                  <button
                    type="button"
                    onClick={clearClientForm}
                    className="rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>

            <div
              ref={importPanelRef}
              className={`${showMobileImport ? 'block' : 'hidden'} xl:block`}
            >
              <div className="space-y-3 rounded-xl border border-dashed border-gray-300 bg-white p-4">
                <div className="flex items-center gap-2">
                  <Upload size={16} className="text-gray-700" />
                  <h4 className="font-medium text-gray-900">Bulk import</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Import `.xlsx`, `.xls`, or `.csv` with columns like `Name`, `Email`, `Phone`, `Notes`, or `Tags`.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImportFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
                >
                  {isImporting ? 'Importing...' : 'Choose spreadsheet'}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr,280px]">
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm sm:flex-1">
                <Search size={16} className="text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search clients by name, email, phone, or tag"
                  className="w-full bg-transparent text-base outline-none"
                />
              </div>
              <select
                value={selectedClientKey}
                onChange={(event) => setSelectedClientKey(event.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-base shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                aria-label="Select client"
              >
                <option value="">Select client</option>
                {filteredSummaries.map((summary) => (
                  <option key={summary.key} value={summary.key}>
                    {summary.displayName}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              {(selectedClientSummary ? [selectedClientSummary] : []).map((summary) => {
                const client = summary.client || {};
                const isSelected = selectedClientKey === summary.key;
                const clientTags = Array.isArray(client.tags) ? client.tags : [];

                return (
                  <div
                    key={summary.key}
                    className={`overflow-hidden rounded-xl border transition ${
                      isSelected
                        ? 'border-indigo-200 bg-indigo-50/40'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedClientKey((prev) => (prev === summary.key ? '' : summary.key))}
                      aria-expanded={isSelected}
                      className="w-full p-4 text-left sm:p-5"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="break-words text-base font-semibold text-gray-900 sm:text-lg">
                            {summary.displayName}
                          </div>
                          <div className="mt-1 break-all text-sm text-gray-500">
                            {client.email || client.phone || 'No contact details'}
                          </div>
                        </div>
                        <div className="shrink-0 rounded-full border border-gray-200 bg-white p-1 text-gray-500">
                          {isSelected ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>
                    </button>

                    {isSelected && (
                      <div className="space-y-4 border-t border-indigo-100 bg-white px-4 py-4 sm:px-5">
                        {clientTags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {clientTags.map((tag) => (
                              <span key={tag} className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <ClientDetails
                          summary={summary}
                          formatCurrency={formatCurrency}
                          paymentMethods={paymentMethods}
                          onRecordPartPayment={onRecordPartPayment}
                          onDownloadInvoice={onDownloadInvoice}
                          onEditTransaction={onEditTransaction}
                        />

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                          {client.id && (
                            <button
                              type="button"
                              onClick={() => onStartSaleForClient(client)}
                              className="rounded-xl bg-green-600 px-4 py-3 font-medium text-white transition hover:bg-green-700"
                            >
                              Record sale
                            </button>
                          )}

                          {client.id && (
                            <button
                              type="button"
                              onClick={() => onStartInvoiceForClient?.(client)}
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 font-medium text-white transition hover:bg-emerald-700"
                            >
                              <FileText size={16} />
                              Create invoice
                            </button>
                          )}

                          {client.id && (
                            <button
                              type="button"
                              onClick={() => openClientEditor(client)}
                              className="rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
                            >
                              Edit client
                            </button>
                          )}

                          {client.id && (
                            <button
                              type="button"
                              onClick={() => handleDeleteClientAction(client.id, summary.displayName, summary.key)}
                              className="rounded-xl border border-red-200 px-4 py-3 font-medium text-red-600 transition hover:bg-red-50"
                            >
                              Delete client
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {searchTerm.trim() && filteredSummaries.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-gray-500">
                  No clients found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientDatabasePanel;
